import React, { useState, useEffect, useCallback } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import { url } from '../../util/config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

const ActionMenu = ({ user, onDeactivate, onAddBalance, onChangeRole }) => {
    const [open, setOpen] = React.useState(false);
    const [menuPos, setMenuPos] = React.useState({ top: 0, left: 0 });
    const ref = React.useRef(null);

    React.useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleToggle = () => {
        if (!open && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const menuHeight = 130;
            const spaceBelow = window.innerHeight - rect.bottom;
            const top = spaceBelow < menuHeight
                ? rect.top - menuHeight + window.scrollY
                : rect.bottom + window.scrollY;
            setMenuPos({ top, left: rect.right - 190 });
        }
        setOpen(!open);
    };

    const menuItems = [
        {
            icon: user.status ? 'bi-person-x' : 'bi-person-check',
            label: user.status ? 'Deactivate User' : 'Activate User',
            color: user.status ? '#dc2626' : '#16a34a',
            onClick: () => { onDeactivate(user._id); setOpen(false); },
        },
        {
            icon: 'bi-wallet2',
            label: 'Add Balance',
            color: '#1565c0',
            onClick: () => { onAddBalance(user._id); setOpen(false); },
        },
        {
            icon: 'bi-person-badge',
            label: 'Change Role',
            color: '#7c3aed',
            onClick: () => { onChangeRole(user._id, user.rolename); setOpen(false); },
        },
    ];

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            <button style={menuStyles.trigger} onClick={handleToggle}>
                <i className="bi bi-three-dots-vertical"></i>
            </button>
            {open && (
                <div style={{ ...menuStyles.menu, position: 'fixed', top: menuPos.top, left: menuPos.left }}>
                    {menuItems.map((item, i) => (
                        <button key={i} style={menuStyles.item} onClick={item.onClick}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                            <span style={{ ...menuStyles.itemIcon, background: item.color + '15', color: item.color }}>
                                <i className={`bi ${item.icon}`}></i>
                            </span>
                            <span style={{ ...menuStyles.itemLabel, color: item.color }}>{item.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const menuStyles = {
    trigger: { background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6b7280', fontSize: '15px' },
    menu: { position: 'absolute', right: 0, top: '36px', background: 'white', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', minWidth: '180px', zIndex: 1000, padding: '6px', overflow: 'hidden' },
    item: { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '8px 10px', border: 'none', background: 'white', borderRadius: '6px', cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s' },
    itemIcon: { width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', flexShrink: 0 },
    itemLabel: { fontSize: '13px', fontWeight: '500' },
};

const ManageUserComponent = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showAddBalanceModal, setShowAddBalanceModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [balance, setBalance] = useState('');
    const [balanceError, setBalanceError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
    const [selectedUserRole, setSelectedUserRole] = useState('');
    const [roles, setRoles] = useState([]);
    const [newRole, setNewRole] = useState('');
    const [roleError, setRoleError] = useState('');
    const itemsPerPage = 10;

    // Single reusable fetchUsers function
    const fetchUsers = useCallback(async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${url}/users`, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token },
            });
            if (!response.ok) throw new Error('Failed to fetch users');
            const data = await response.json();
            setUsers(data);
        } catch (err) {
            console.error('Error fetching users:', err);
            setError('Failed to load users. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const openAddBalanceModal = (userId) => {
        setSelectedUserId(userId);
        setBalance('');
        setBalanceError('');
        setShowAddBalanceModal(true);
    };

    const openChangeRoleModal = async (userId, currentRole) => {
        setSelectedUserId(userId);
        setSelectedUserRole(currentRole);
        setNewRole('');
        setRoleError('');
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/role`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch roles');
            const data = await response.json();
            setRoles(data || []);
            setShowChangeRoleModal(true);
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Failed to load roles');
        }
    };

    const filteredUsers = users.filter(
        (user) =>
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

    const handleSearchTermChange = (term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    const handleAddBalance = async (userId) => {
        if (!/^\d+$/.test(balance)) { setBalanceError('Please enter a valid numeric value.'); return; }
        if (balance.length > 7) { setBalanceError('Balance cannot exceed 7 digits.'); return; }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/addBalance`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ userId, balance: parseInt(balance, 10) }),
            });
            if (response.ok) {
                const successData = await response.json();
                toast.success(successData.message || 'Balance added successfully!', { autoClose: 3000 });
                await fetchUsers();
                setShowAddBalanceModal(false);
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || 'Failed to add balance. Please try again.', { autoClose: 3000 });
                setBalanceError(errorData.error || 'Failed to add balance.');
            }
        } catch (error) {
            console.error('Error adding balance:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        }
    };

    const handleChangeRole = async () => {
        if (!newRole) { setRoleError('Please select a new role.'); return; }
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/roleUpdate`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ userId: selectedUserId, roleId: newRole }),
            });
            if (response.ok) {
                const successData = await response.json();
                toast.success(successData.message || 'Role updated successfully!', { autoClose: 3000 });
                await fetchUsers();
                setShowChangeRoleModal(false);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Failed to update role.', { autoClose: 3000 });
                setRoleError(errorData.message || 'Failed to update role.');
            }
        } catch (error) {
            console.error('Error updating role:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        }
    };

    const handleDeactivateUser = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/statusUpdate`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ userId }),
            });
            if (response.ok) {
                const dt = await response.json();
                toast.success(dt.message, { autoClose: 3000 });
                await fetchUsers();
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Failed to update status.', { autoClose: 3000 });
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        }
    };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Manage Users</h5>
                        <p style={styles.pageSubtitle}>View and manage all users under your account</p>
                    </div>
                    <div style={styles.headerActions}>
                        <button style={styles.btnOutline} onClick={() => navigate('/whatsappprofile')}>
                            <i className="bi bi-whatsapp" style={{ marginRight: 6 }}></i>
                            WhatsApp Profile
                        </button>
                        <button style={styles.btnPrimary} onClick={() => navigate('/createuser')}>
                            <i className="bi bi-person-plus" style={{ marginRight: 6 }}></i>
                            Add New User
                        </button>
                    </div>
                </div>

                {/* Search */}
                <div style={styles.searchRow}>
                    <div style={styles.searchWrapper}>
                        <i className="bi bi-search" style={styles.searchIcon}></i>
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={(e) => handleSearchTermChange(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                    <span style={styles.resultCount}>{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Table card */}
                <div style={styles.card}>
                    {isLoading ? (
                        <div style={styles.centerState}>
                            <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p style={styles.stateText}>Loading users…</p>
                        </div>
                    ) : error ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-exclamation-circle" style={{ fontSize: 32, color: '#ef4444' }}></i>
                            <p style={{ ...styles.stateText, color: '#ef4444' }}>{error}</p>
                            <button style={styles.btnPrimary} onClick={fetchUsers}>Retry</button>
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-people" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                            <p style={styles.stateText}>{searchTerm ? 'No users match your search.' : 'No users found. Add your first user.'}</p>
                            {!searchTerm && <button style={styles.btnPrimary} onClick={() => navigate('/createuser')}>Add New User</button>}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHead}>
                                        <th style={styles.th}>Name</th>
                                        <th style={styles.th}>Email</th>
                                        <th style={styles.th}>Mobile</th>
                                        <th style={styles.th}>Country</th>
                                        <th style={styles.th}>Balance</th>
                                        <th style={styles.th}>Role</th>
                                        <th style={styles.th}>Status</th>
                                        <th style={styles.th}>Created On</th>
                                        <th style={styles.th}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentUsers.map((user) => (
                                        <tr key={user._id} style={styles.tr}>
                                            <td style={styles.td}>{user.name}</td>
                                            <td style={styles.td}>{user.email}</td>
                                            <td style={styles.td}>{user.mobile_no}</td>
                                            <td style={styles.td}>{user.country}</td>
                                            <td style={styles.td}>₹{Number(user.balance).toLocaleString()}</td>
                                            <td style={styles.td}>{user.rolename}</td>
                                            <td style={styles.td}>
                                                <span style={{ ...styles.badge, ...(user.status ? styles.badgeActive : styles.badgeInactive) }}>
                                                    {user.status ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{user.createdAt}</td>
                                            <td style={styles.td}>
                                                <ActionMenu
                                                    user={user}
                                                    onDeactivate={handleDeactivateUser}
                                                    onAddBalance={openAddBalanceModal}
                                                    onChangeRole={openChangeRoleModal}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!isLoading && !error && filteredUsers.length > 0 && (
                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(page) => setCurrentPage(page)}
                    />
                )}

                <ToastContainer />

                {/* Change Role Modal */}
                {showChangeRoleModal && (
                    <div style={styles.modalOverlay}>
                        <div style={styles.modalBox}>
                            <div style={styles.modalHeader}>
                                <h6 style={styles.modalTitle}>Change Role</h6>
                                <button style={styles.closeBtn} onClick={() => setShowChangeRoleModal(false)}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                            <div style={styles.modalBody}>
                                <p style={styles.currentRoleText}>
                                    Current Role: <strong>{selectedUserRole}</strong>
                                </p>
                                <label style={styles.label}>Select New Role</label>
                                <select
                                    style={styles.select}
                                    value={newRole}
                                    onChange={(e) => { setNewRole(e.target.value); setRoleError(''); }}
                                >
                                    <option value="">Select Role</option>
                                    {roles.map((role) => (
                                        <option key={role._id} value={role._id}>{role.role_name}</option>
                                    ))}
                                </select>
                                {roleError && <span style={styles.fieldError}>{roleError}</span>}
                            </div>
                            <div style={styles.modalFooter}>
                                <button style={styles.btnSecondary} onClick={() => setShowChangeRoleModal(false)}>Cancel</button>
                                <button style={styles.btnPrimary} onClick={handleChangeRole}>Update Role</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add Balance Modal */}
                {showAddBalanceModal && (
                    <div style={styles.modalOverlay}>
                        <div style={styles.modalBox}>
                            <div style={styles.modalHeader}>
                                <h6 style={styles.modalTitle}>Add Balance</h6>
                                <button style={styles.closeBtn} onClick={() => setShowAddBalanceModal(false)}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                            <div style={styles.modalBody}>
                                <label style={styles.label}>Enter Amount</label>
                                <input
                                    type="text"
                                    style={{ ...styles.input, ...(balanceError ? styles.inputError : {}) }}
                                    value={balance}
                                    placeholder="e.g. 500"
                                    maxLength="7"
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^\d*$/.test(value)) {
                                            setBalance(value);
                                            setBalanceError('');
                                        } else {
                                            setBalanceError('Only numeric values are allowed.');
                                        }
                                    }}
                                />
                                {balanceError && <span style={styles.fieldError}>{balanceError}</span>}
                            </div>
                            <div style={styles.modalFooter}>
                                <button style={styles.btnSecondary} onClick={() => setShowAddBalanceModal(false)}>Cancel</button>
                                <button style={styles.btnPrimary} onClick={() => handleAddBalance(selectedUserId)}>Add Balance</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    headerActions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
    searchRow: { display: 'flex', alignItems: 'center', gap: '12px' },
    searchWrapper: { position: 'relative', flex: 1, maxWidth: '360px' },
    searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '14px' },
    searchInput: { width: '100%', padding: '9px 12px 9px 36px', fontSize: '14px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', color: '#111827' },
    resultCount: { fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' },
    card: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    tableHead: { background: '#eff6ff' },
    th: { padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', borderBottom: '2px solid #bfdbfe' },
    tr: { borderBottom: '1px solid #f3f4f6' },
    td: { padding: '12px 14px', color: '#374151', verticalAlign: 'middle' },
    badge: { display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
    badgeActive: { background: '#dcfce7', color: '#16a34a' },
    badgeInactive: { background: '#fee2e2', color: '#dc2626' },
    actionBtn: { background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#374151', borderRadius: '6px', fontSize: '13px', padding: '5px 12px', fontWeight: '500' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    btnOutline: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'white', color: '#1565c0', border: '1.5px solid #1565c0', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 },
    modalBox: { background: 'white', borderRadius: '12px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', overflow: 'hidden' },
    modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc' },
    modalTitle: { margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '14px', padding: 0 },
    modalBody: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '8px' },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #f3f4f6' },
    currentRoleText: { fontSize: '13px', color: '#6b7280', margin: 0 },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
    input: { padding: '10px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827' },
    inputError: { borderColor: '#ef4444' },
    select: { padding: '10px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827', background: 'white' },
    fieldError: { fontSize: '12px', color: '#ef4444' },
};

export default ManageUserComponent;
